/**
 * The People area's words: the register, the person visit and its boxes.
 * Every sentence a person reads here is said once, in the user's words, with
 * no em or en dash (C2).
 */
export const peopleWords = {
  // Shared mechanics
  check: "Check",
  keepMine: "Keep mine",
  loadTheirs: "Load theirs",
  notRecorded: "Not recorded",
  notSaved: "Not saved.",
  notConfirmed: "Not confirmed",
  couldNotRefresh: "Couldn't refresh. A later change isn't shown.",
  thisDidNotGoThrough: "This did not go through. Your entry is kept; send it when ready.",
  thisValueWasNotSaved: "This value was not saved. Try again.",
  changedSinceYouReadIt: "This changed since you read it. Your text is kept. Choose before it saves.",
  open: "Open",
  person: "Person",
  hide: "Hide",
  ready: "Ready",

  // The person
  personLabel: "Person",
  personJob: "The facts about this person, edited once, where every page reads them.",
  personDetail: "Used on every page that names or contacts this person.",
  personUnavailable: "Couldn't read this person",
  personUnavailableDetail: "Nothing about this person is inferred from the failed read.",
  fullName: "Full name",
  preferredName: "Preferred name",
  honorific: "Honorific",
  email: "Email",
  none: "None",

  // Identity and access
  accessLabel: "Identity and access",
  accessJob: "Whether this person can sign in, and how.",
  accessUnavailable: "Couldn't read the sign-in state",
  accessUnavailableDetail: "Nothing about access is inferred from the failed read.",
  invitationBelow: "Their invitation is below.",
  inviteBelow: "No invitation yet. Invite them below.",
  noInvitationWork: "No invitation yet.",
  workspaceNeverRead: "Google has never been read back for this account.",

  // LMS sign-in: one fact, said in one vocabulary. A retirement on record is a suspended sign-in.
  signInActive: "LMS sign-in active",
  signInSuspended: "LMS sign-in suspended",
  noSignIn: "No LMS sign-in",
  withdrawLabel: "Suspend LMS sign-in",
  restoreAccessLabel: "Restore LMS sign-in",
  withdrawJob: "Block or allow this person's LMS sign-in from their next request.",
  withdrawDetail: "Blocks sign-in from their next request.",
  withdrawnDetail: "LMS sign-in is suspended. Academic records and Google accounts are kept.",
  withdrawPart: "Sign-in",
  withdrawPartSummary: "Membership is read on every request, so the change holds from their next one.",
  withdrawCap: "When you suspend LMS sign-in",
  restoreAccessCap: "When you restore LMS sign-in",
  withdrawBlocked: "LMS sign-in is blocked from their next request",
  sessionNotGuaranteed: "A sign-in they already hold may stay open until it expires",
  recordsKept: "Records, enrollments and the account are kept",
  accessReturns: "LMS sign-in is allowed from their next request, with the same role",
  selfBlocked: "This is your own account. Another administrator changes it.",
  superAdminBlocked: "This account holds tenant super admin authority. Use manual containment and the governed recovery path.",
  adminAuthorityBlocked: "Only the institution's super administrator can change another administrator's sign-in.",
  withdrawRefused: "Not changed.",
  withdrawUnknown: "Changing sign-in",
  withdrawUnknownSentence: "We could not confirm whether sign-in changed. Check before changing it again.",
  withdrawCheckNote: "Reads this account. Nothing is changed again.",

  accountRetired: "Account retired",
  restoreDetail: "Restores access with the reason on record.",
  whyPart: "Why",
  written: "Written",
  needed: "Needed",

  // Archived people keep their sign-in work in place, blocked until they are restored (F5).
  archivedPart: "Shown in People",
  archivedWord: "Archived",
  restoreFirst: "Restore this person to People first.",
  restoreFirstState: "Restore to People first",
  restoreToPeople: "Restore to People",
  archivedNoInvitation: "No invitation yet. Restore this person to People to invite them.",
  archivedInviteSummary: "Archived people receive no new sign-in invitation or setup email. Restoring them changes nothing else.",

  historyLabel: "Account lifecycle history",
  historyJob: "What happened to this account, and why.",
  historyEmpty: "No lifecycle events recorded.",

  resendLabel: "Send a fresh setup email",
  resendJob: "Send a fresh setup email on this person's invitation.",
  resendDetail: "Their invitation is the record. Sending again changes nothing else.",
  invitationPart: "Invitation",
  invitationPartSummary: "Its history, resend and revoke are on the invitation's own page.",
  readyToSend: "Ready to send",
  sent: "Sent",
  notSent: "Not sent",
  resendVerb: "Send setup email",
  resendCap: "When you send",
  nobodyMatched: "Nobody is matched by email or name.",
  resendRefused: "Not sent.",
  resendUnknown: "Sending the setup email",
  resendUnknownSentence: "We could not confirm whether the email was sent. Check before sending again.",
  resendCheckNote: "Reads the invitation. Nothing is sent again.",
  invitationNowSent: "Their invitation now reads Invitation sent",

  invitationListLabel: "Current invitation",
  invitationListJob: "This person's current invitation, opened in its own record.",
  setupInvitation: "Setup invitation",
  openInvitation: "Open invitation",
  notSentYet: "Not sent yet",

  inviteLabel: "Invite to sign in",
  inviteJob: "Invite this person to sign in with a managed account.",
  noSignInYet: "No sign-in yet",
  inviteDetail: "The courses are theirs either way.",
  inviteOpenDetail: "A managed account is created and a setup email goes to the address below.",
  continueInvitation: "Continue the invitation",
  accountPart: "Account",
  givenName: "Given name",
  familyName: "Family name",
  managedAccount: "Managed account",
  deliveryEmail: "Delivery email",
  managedAccountNeeded: "Managed account needed",
  enterAccountAndDelivery: "Enter the managed account and a delivery email.",
  noManagedDomain: "This address can't create managed accounts. Sign in on your institution's own address.",
  inviteVerb: "Send invitation",
  inviteCap: "When you send",
  inviteRefused: "Not sent.",
  inviteUnknown: "Sending the invitation",
  inviteUnknownSentence: "We could not confirm whether the invitation was made. Check before sending again.",
  inviteCheckNote: "Reads what this send made. Nothing is sent again.",
  loginJoinsRecord: "The login joins this record when it is used",
  setupEmailSent: "Sent a setup email",

  // Refused visit
  refused: "You can't open this here, or it couldn't be read. Nothing about it is shown.",
} as const;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
const first = (name: string) => name.trim().split(/\s+/)[0] ?? name;

export type InviteRole = "student" | "instructor" | "admin";
const ROLE_WORD: Record<InviteRole, string> = { student: "student", instructor: "instructor", admin: "administrator" };
const ROLE_EFFECT: Record<InviteRole, string> = {
  student: "Sign in as a student, joined to their student record",
  instructor: "Sign in as an instructor, to teach the courses they are assigned",
  admin: "Sign in as an Administrator, with authority over people, courses and settings across the institution",
};

export const peopleText = {
  first,
  plural,
  subject: (name: string, relationship: string) => (relationship ? `${name} · ${relationship}` : name),
  role: (role: string) => `Role: ${role}`,
  accessProse: (lms: string, workspace: string | null) => (workspace ? `${lms} ${workspace}` : lms),
  workspaceChecked: (day: string) => `Google Workspace as last checked ${day}`,
  relationshipsToResolve: (count: number) => `${plural(count, "relationship", "relationships")} to resolve`,
  resendEffect: (name: string) => `Send a fresh setup email to ${name}, to the address on their invitation`,
  resent: (name: string) => `Sent a setup email to ${first(name)}`,
  resentLine: (name: string) => `Sent a fresh setup email to ${name}`,
  checkedSent: "Checked: the email was sent",
  lastSent: (day: string) => `Last sent ${day}`,
  expires: (day: string) => `Expires ${day}`,
  expired: (day: string) => `Expired ${day}`,
  /** The account an invitation makes, named by the LMS role it carries. */
  createAccount: (name: string, role: InviteRole) => `Create a managed ${ROLE_WORD[role]} account for ${first(name)}`,
  createdAccount: (name: string, role: InviteRole) => `Created a managed ${ROLE_WORD[role]} account for ${first(name)}`,
  roleEffect: (role: InviteRole) => ROLE_EFFECT[role],
  archivedOn: (day: string) => `Archived from People on ${day}`,
  sendSetupEmail: "Send a setup email; the login joins this record",
  deliveryNotSent: (word: string) => `The setup email reads ${word}. Send it again from their access.`,
};

/** Person management's words: the Manage menu and its lifecycle Tasks. */
export const manageWords = {
  label: "Manage person",
  job: "This person's place in People and their lifecycle acts.",
  archive: "Archive person",
  archiveDoes: "Hide them from People. Sign-in, courses and standing stay as they are.",
  restore: "Restore to People",
  restoreDoes: "List them in People again. Sign-in and standing stay as they are.",
  addTeaching: "Add teaching profile",
  addTeachingDoes: "Let this same person teach. Sign-in and role stay as they are.",
  offboard: "Offboard person",
  offboardDoes: "Review and end their current courses, teaching, invitations and sign-in, then archive.",
  remove: "Delete person",
  removeDoes: "Permanently delete a record nothing depends on.",
  archiveInstead: "Archive instead",
  offboardInstead: "Offboard instead",

  archiveJob: "Change whether People lists this person.",
  archiveDetail: "This changes only whether People lists them.",
  stillInPlace: "Still in place",
  unchanged: "Unchanged",
  stillInPlaceIntro: "Archiving leaves these as they are. Offboarding ends them after a review.",
  canSignIn: "Can sign in to the LMS",
  archiveEffects: [
    "Hide this person from People and from new selection lists",
    "Keep their LMS sign-in, student standing and course records as they are",
    "Find them under Archived or Everyone and restore them at any time",
  ],
  restoreEffects: [
    "List this person in People again",
    "Keep their LMS sign-in and student standing as they are; each is restored in its own section",
  ],
  archivedRemains: "Restore to People lists them again. It changes nothing else.",
  restoredRemains: "Their LMS sign-in and student standing are unchanged.",
  refreshPerson: "Refresh person",
  latestRecord: "Latest record",
  readAgain: "Read again",

  teachingJob: "Add a teaching relationship without duplicating the person.",
  teachingDetail: "A teaching profile keeps public teaching facts. It grants no sign-in or course assignment.",
  profileExists: "Profile exists",
  noProfile: "No teaching profile",
  profileAlready: "This person already has a teaching profile.",
  nameFirst: "Enter this person's full name in Person first.",
  teachingEffects: ["Add a teaching profile to this same person", "Keep their sign-in, role and student record; assign courses separately"],

  roleJob: "Change this person's LMS role.",
  roleLabel: "Change role",
  rolePart: "LMS role",
  newRole: "New role",
  chooseRole: "Choose a role",
  chooseNewRole: "Choose the new role.",
  roleNote: "Administrator changes require the institution's super administrator. Student access requires a student record. Academic records are kept.",
  roleSelfBlocked: "This is your own account. Another administrator changes its role.",
  roleSuperAdminBlocked: "The tenant super administrator's role is managed outside the application.",
  roleAdminBlocked: "Only the institution's super administrator can change another administrator's role.",
  roleKept: "Keep their sign-in status, course records and Google account",
  roleChanged: "LMS role changed",
  roleCap: "When you change the role",

  offboardJob: "End current responsibilities and access together.",
  offboardDetail: "Review what they hold now. Completed and historical records are kept.",
  reviewEffects: "Review effects",
  reviewOffboarding: "Review offboarding",
  reviewPart: "What offboarding ends",
  review: "Review",
  reviewUnavailable: "Couldn't review this person's responsibilities",
  reviewUnavailableDetail: "Refresh the review before continuing.",
  refreshReview: "Refresh review",
  nothingCurrent: "Nothing current to end. Offboarding archives this person.",
  teaching: "Teaching",
  courses: "Courses as a student",
  standing: "Student standing",
  invitations: "Invitations",
  signIn: "LMS sign-in",
  replacePrimary: "Primary instructor; choose who takes over",
  draftPrimary: "Primary instructor of a draft; teaching ends with no replacement",
  endsTeaching: "Co-instructor; teaching ends",
  enrollmentEnds: "Enrollment withdrawn",
  revoked: "Revoked",
  chooseReplacement: "Choose a primary instructor",
  noReplacement: "No other teacher who can sign in may take over this course yet. Assign one in the course first.",
  signInWillSuspend: "Will be suspended",
  signInAlreadySuspended: "Already suspended",
  kept: "Kept: completed and historical academic work, results and Google accounts.",
  reason: "Reason for offboarding",
  suspendNow: "To block sign-in now while you resolve this, use Suspend LMS sign-in in Access.",
  readReviewFirst: "Read the current review before continuing.",
  replaceEach: "Choose a replacement for each current primary teaching assignment.",
  enterReason: "Enter the reason for offboarding.",
  offboardCap: "When you offboard",
  unreadEffect: "End what the review lists, then archive this person",
  archiveEffect: "Archive this person from People",
  keepArchived: "Keep this person archived",
  keepEffect: "Keep completed and historical academic work, results and Google accounts",
  standingEffect: "Set student standing to Withdrawn",
  suspendEffect: "Suspend LMS sign-in",
  offboarded: "Person offboarded",
  standingWithdrawn: "Student standing set to Withdrawn",
  signInSuspendedLine: "LMS sign-in suspended",
  signInStaysSuspended: "LMS sign-in stays suspended",
  archivedLine: "Archived from People; completed and historical academic work kept",

  deleteJob: "Delete an unused or mistaken record.",
  deleteDetail: "Deletion is permanent. Records with academic or account history can be archived and kept accessible instead.",
  reviewDeletion: "Review deletion",
  unused: "Unused record",
  reviewDependencies: "Review dependencies",
  deletePart: "Records to keep",
  readyToDelete: "Ready to delete",
  nothingRetained: "No retained academic or account dependencies prevent deleting this person.",
  archiveWhileRetained: "Archive this person to remove them from working lists while these records remain.",
  offboardWhileCurrent: "They still hold current work or sign-in. Offboard ends it and archives them.",
  mustRetain: "These records must be retained. Archive this person to remove them from working lists.",
  readDeletionFirst: "Read the deletion review before continuing.",
  deleteVerb: "Delete person permanently",
  deleteCap: "When you delete",
  deleteEffects: ["Delete this unused person and their unused records", "Keep reserved student identifiers and the removal audit", "Keep external sign-in identities and Google accounts"],
  deleted: ["Person deleted", "Reserved identifiers and the removal audit kept"],
} as const;

export const manageText = {
  teachesSections: (count: number) => `Teaching records in ${plural(count, "section", "sections")}`,
  standing: (word: string) => `Student standing: ${word}`,
  standingChanges: (word: string) => `Changes from ${word} to Withdrawn`,
  standingStays: (word: string) => `Stays ${word}`,
  replacementFor: (label: string) => `Replacement for ${label}`,
  currentRole: (role: string) => `Current role: ${role}. Academic relationships and sign-in are kept.`,
  roleEffect: (label: string, admin: boolean) =>
    admin ? `Change their LMS role to ${label}, with authority over people, courses and settings across the institution` : `Change their LMS role to ${label}`,
  withdrawEnrollments: (count: number) => `Withdraw ${plural(count, "current course enrollment", "current course enrollments")}`,
  handOn: (count: number) => `Hand ${plural(count, "primary teaching assignment", "primary teaching assignments")} to the teachers chosen`,
  endTeaching: (count: number) => `End ${plural(count, "teaching assignment", "teaching assignments")}`,
  revokeInvitations: (count: number) => `Revoke ${plural(count, "pending invitation", "pending invitations")}`,
  enrollmentsWithdrawn: (count: number) => `${plural(count, "current enrollment", "current enrollments")} withdrawn`,
  teachingHandled: (count: number) => `${plural(count, "teaching assignment", "teaching assignments")} handed on or ended`,
  invitationsRevoked: (count: number) => `${plural(count, "pending invitation", "pending invitations")} revoked`,
  /** How to bring an offboarded person back: each fact restores in its own place; nothing re-enrolls. */
  recovery: ({ signIn, standing, courses }: { signIn: boolean; standing: boolean; courses: boolean }) =>
    [
      "To bring them back, Restore to People",
      signIn ? "then Restore LMS sign-in in Access" : null,
      standing ? "and change their standing in Student record" : null,
    ].filter(Boolean).join(", ") + (courses ? ". Courses are not re-enrolled." : "."),
};
